/**
 * Lightweight telemetry for the purchase tracking kill-switch.
 *
 * Buffers events client-side and flushes them in small batches to the
 * `record_killswitch_telemetry` RPC (anon-callable, SECURITY DEFINER).
 *
 * Fail-silent: telemetry failures must never affect tracking behaviour.
 */
import { supabase } from "@/integrations/supabase/client";

export type KillSwitchTelemetryEvent =
  | "rpc_fetch"
  | "gate_allowed"
  | "gate_blocked"
  | "toggle";

interface TelemetryPayload {
  event_type: KillSwitchTelemetryEvent;
  paused_state?: boolean | null;
  cache_hit?: boolean | null;
  latency_ms?: number | null;
  error_code?: string | null;
  extra?: Record<string, unknown>;
}

const BUFFER: TelemetryPayload[] = [];
const FLUSH_INTERVAL_MS = 5_000;
const MAX_BUFFER = 25;
const SESSION_KEY = "zooi_pks_telemetry_session";
const LOG_PREFIX = "[purchase-killswitch-telemetry]";

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sessionHash: string | null = null;

function getSessionHash(): string | null {
  if (sessionHash) return sessionHash;
  try {
    if (typeof sessionStorage === "undefined") return null;
    let v = sessionStorage.getItem(SESSION_KEY);
    if (!v) {
      v =
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2)) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, v);
    }
    sessionHash = v.slice(0, 24); // bounded length, opaque
    return sessionHash;
  } catch {
    return null;
  }
}

export interface KillSwitchTelemetryFlushResult {
  attempted: number;
  succeeded: number;
  failed: number;
  sessionHash: string | null;
}

async function flush(): Promise<KillSwitchTelemetryFlushResult> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const session = getSessionHash();
  if (BUFFER.length === 0) {
    return { attempted: 0, succeeded: 0, failed: 0, sessionHash: session };
  }
  const batch = BUFFER.splice(0, BUFFER.length);
  // Fire-and-forget; one RPC per event keeps the schema simple. The buffer
  // dedupes bursts (e.g. 25 cart actions) into a single tick.
  const results = await Promise.allSettled(
    batch.map((p) =>
      supabase.rpc("record_killswitch_telemetry" as never, {
        _event_type: p.event_type,
        _paused_state: p.paused_state ?? null,
        _cache_hit: p.cache_hit ?? null,
        _latency_ms: p.latency_ms ?? null,
        _error_code: p.error_code ?? null,
        _session_hash: session,
        _extra: p.extra ?? {},
      } as never),
    ),
  );
  let succeeded = 0;
  let failed = 0;
  for (const r of results) {
    if (r.status === "fulfilled" && !(r.value as { error?: unknown })?.error) succeeded++;
    else failed++;
  }
  if (failed > 0) {
    // eslint-disable-next-line no-console
    console.warn(`${LOG_PREFIX} flush: ${failed}/${batch.length} failed`);
  }
  return { attempted: batch.length, succeeded, failed, sessionHash: session };
}

/** Force-flush buffered telemetry now and return per-event delivery counts. */
export async function flushKillSwitchTelemetry(): Promise<KillSwitchTelemetryFlushResult> {
  return flush();
}

/** Snapshot of current frontend buffer state — used by sanity checks. */
export function getKillSwitchTelemetryBufferStatus(): {
  size: number;
  sessionHash: string | null;
  hasPendingTimer: boolean;
} {
  return {
    size: BUFFER.length,
    sessionHash: getSessionHash(),
    hasPendingTimer: flushTimer !== null,
  };
}

function scheduleFlush() {
  if (flushTimer) return;
  if (BUFFER.length >= MAX_BUFFER) {
    void flush();
    return;
  }
  flushTimer = setTimeout(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);
}

/** Record a telemetry event (fail-silent, never throws). */
export function recordKillSwitchTelemetry(payload: TelemetryPayload): void {
  try {
    BUFFER.push(payload);
    scheduleFlush();
  } catch {
    /* swallow */
  }
}

// Flush on page hide so we don't lose buffered events.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    void flush();
  });
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });
}
